'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useMemo } from 'react';
import { useSocialLinks, findSocialLink } from '@/hooks/useSocialLinks';
import { formatSocialHref, formatSocialDisplayText } from '@/utils/socialLink';

const SOCIAL_STYLE_MAP: Record<string, { bg: string; hover: string; iconColor: string; iconClass?: string }> = {
  FACEBOOK: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-facebook-fill' },
  FACEBOOK_PAGE: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-pages-line' },
  FACEBOOK_GROUP: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-group-line' },
  INSTAGRAM: { bg: 'bg-[#E1306C]/10', hover: 'hover:bg-[#E1306C]', iconColor: 'text-[#E1306C]', iconClass: 'ri-instagram-line' },
  TWITTER: { bg: 'bg-[#000]/10', hover: 'hover:bg-[#000]', iconColor: 'text-[#000]', iconClass: 'ri-twitter-x-line' },
  YOUTUBE: { bg: 'bg-[#FF0000]/10', hover: 'hover:bg-[#FF0000]', iconColor: 'text-[#FF0000]', iconClass: 'ri-youtube-fill' },
  TIKTOK: { bg: 'bg-[#010101]/10', hover: 'hover:bg-[#010101]', iconColor: 'text-[#010101]', iconClass: 'ri-tiktok-line' },
  PINTEREST: { bg: 'bg-[#BD081C]/10', hover: 'hover:bg-[#BD081C]', iconColor: 'text-[#BD081C]', iconClass: 'ri-pinterest-line' },
  WHATSAPP: { bg: 'bg-[#25D366]/10', hover: 'hover:bg-[#25D366]', iconColor: 'text-[#25D366]', iconClass: 'ri-whatsapp-line' },
  TELEGRAM: { bg: 'bg-[#229ED9]/10', hover: 'hover:bg-[#229ED9]', iconColor: 'text-[#229ED9]', iconClass: 'ri-telegram-line' },
  EMAIL: { bg: 'bg-[#F4A261]/10', hover: 'hover:bg-[#F4A261]', iconColor: 'text-[#F4A261]', iconClass: 'ri-mail-line' },
  PHONE: { bg: 'bg-[#2A9D8F]/10', hover: 'hover:bg-[#2A9D8F]', iconColor: 'text-[#2A9D8F]', iconClass: 'ri-phone-line' },
  WEBSITE: { bg: 'bg-[#5E60CE]/10', hover: 'hover:bg-[#5E60CE]', iconColor: 'text-[#5E60CE]', iconClass: 'ri-global-line' },
  OTHER: { bg: 'bg-warm-brown/10', hover: 'hover:bg-warm-brown', iconColor: 'text-warm-brown', iconClass: 'ri-links-line' }
};

const DEFAULT_SOCIAL_STYLE = { bg: 'bg-warm-brown/10', hover: 'hover:bg-warm-brown', iconColor: 'text-warm-brown', iconClass: 'ri-external-link-line' };

const FALLBACK_SOCIALS = [
  {
    key: 'facebook-fallback',
    label: 'Facebook',
    href: 'https://facebook.com/61586771054665',
    bg: 'bg-[#1877F2]/10',
    hover: 'hover:bg-[#1877F2]',
    iconColor: 'text-[#1877F2]',
    iconClass: 'ri-facebook-fill'
  },
  {
    key: 'instagram-fallback',
    label: 'Instagram',
    href: 'https://instagram.com/LINDYbalinesecats',
    bg: 'bg-[#E1306C]/10',
    hover: 'hover:bg-[#E1306C]',
    iconColor: 'text-[#E1306C]',
    iconClass: 'ri-instagram-line'
  },
  {
    key: 'pinterest-fallback',
    label: 'Pinterest',
    href: 'https://pinterest.com/LINDYbalinesecats',
    bg: 'bg-[#BD081C]/10',
    hover: 'hover:bg-[#BD081C]',
    iconColor: 'text-[#BD081C]',
    iconClass: 'ri-pinterest-line'
  }
];

export default function SiteFooter() {
  const { links: managedLinks } = useSocialLinks();

  const socials = useMemo(() => {
    const active = managedLinks
      .filter(link => link && (link.isActive ?? true))
      .filter(link => !['EMAIL', 'PHONE', 'WHATSAPP'].includes(link.platform.toUpperCase()))
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    if (active.length === 0) {
      return FALLBACK_SOCIALS;
    }

    return active.map(link => {
      const platformKey = link.platform.toUpperCase();
      const style = SOCIAL_STYLE_MAP[platformKey] ?? DEFAULT_SOCIAL_STYLE;
      const fallbackLabel = platformKey.replace(/_/g, ' ');
      const iconClass = link.iconName
        ? (link.iconName.startsWith('ri-') ? link.iconName : `ri-${link.iconName}`)
        : style.iconClass ?? DEFAULT_SOCIAL_STYLE.iconClass;

      return {
        key: link.id,
        label: formatSocialDisplayText(link, fallbackLabel),
        href: formatSocialHref(link),
        bg: style.bg,
        hover: style.hover,
        iconColor: style.iconColor,
        iconClass
      };
    });
  }, [managedLinks]);

  const emailLink = findSocialLink(managedLinks, 'EMAIL');
  const phoneLink = findSocialLink(managedLinks, 'PHONE');

  const contactInfo = useMemo(() => {
    const info = [
      {
        key: 'email',
        label: 'Email',
        value: 'lindywhite948@gmail.com',
        href: 'mailto:lindywhite948@gmail.com',
        icon: (
          <svg className="w-4 h-4 text-soft-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      }
    ];

    // Only display phone if it exists and is NOT the old number we want to hide
    const oldNumberString = '6064652993';
    if (phoneLink && !phoneLink.url.includes(oldNumberString)) {
      info.push({
        key: 'phone',
        label: 'Phone',
        value: formatSocialDisplayText(phoneLink, ''),
        href: formatSocialHref(phoneLink),
        icon: (
          <svg className="w-4 h-4 text-soft-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        )
      });
    }

    return info;
  }, [managedLinks, emailLink, phoneLink]);

  const quickLinks = [
    { href: '/', label: 'Home' },
    { href: '/kittens', label: 'Available Kittens' },
    { href: '/process', label: 'Adoption Process' },
    { href: '/training', label: 'Training & Care' },
    { href: '/gallery', label: 'Gallery Preview' },
  ];
  return (
    <footer className="relative bg-gradient-to-br from-warm-brown via-brown-light to-brown-dark text-cream-light mt-20 overflow-hidden">
      <div className="absolute inset-0 bg-texture-paper opacity-5"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-light/30 to-transparent"></div>
      <div className="absolute top-20 left-10 w-32 h-32 bg-soft-gold/5 rounded-full blur-3xl animate-pulse-gentle"></div>
      <div className="absolute bottom-20 right-10 w-24 h-24 bg-cream-light/10 rounded-full blur-2xl animate-float"></div>

      <div className="relative max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg overflow-hidden">
                <Image
                  src="/logo.png"
                  alt="LINDY Logo"
                  width={44}
                  height={44}
                  className="object-contain w-11 h-11"
                  priority
                />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl text-cream-light">LINDY Balinese Cats</h3>
                <p className="text-cream/80 text-sm">Grace • Devotion • Companionship</p>
              </div>
            </div>
            <p className="text-cream/80 leading-relaxed mb-6">
              We raise extraordinary Balinese companions in a serene, family-centered environment. Our passion blends refined bloodlines, holistic care, and attentive socialization to deliver balanced, affectionate felines.
            </p>
            <div className="flex space-x-4">
              {socials.map((social) => (
                <motion.a
                  key={social.key}
                  href={social.href}
                  className={`w-12 h-12 ${social.bg} ${social.hover} rounded-full flex items-center justify-center transition-all duration-300 group`}
                  aria-label={social.label}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <i className={`${social.iconClass} text-lg ${social.iconColor} group-hover:text-white transition-colors duration-300`}></i>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="font-heading font-semibold text-cream-light mb-6">Explore LINDY</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-cream/80 hover:text-soft-gold transition-colors duration-300 text-sm font-medium hover:translate-x-1 inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h4 className="font-heading font-semibold text-cream-light mb-6">Connect Directly</h4>
            <div className="space-y-3 text-sm">
              {contactInfo.map((info) => (
                <div key={info.key} className="flex items-center space-x-3 text-cream/80">
                  {info.icon}
                  <span>{info.value}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <h4 className="font-heading font-semibold text-cream-light mb-6">Join Our Inner Circle</h4>
            <p className="text-cream/80 text-sm mb-4">
              Receive litter announcements, wellness tips, and exclusive invitations to LINDY house visits.
            </p>
            <Link
              href="/waiting-list"
              className="inline-flex items-center px-5 py-3 bg-gradient-to-r from-soft-gold to-gold-dark text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
            >
              Join the Waiting List
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="pt-8 border-t border-cream/20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-cream/60 text-sm">
              2025 LINDY Balinese Cats. Crafted with devotion for discerning feline families.
            </p>
            <div className="flex items-center space-x-6 text-xs text-cream/60">
              <Link href="/privacy" className="hover:text-soft-gold transition-colors duration-300">Privacy Policy</Link>
              <Link href="/terms" className="hover:text-soft-gold transition-colors duration-300">Terms of Service</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
